import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { HomePopup } from '../../components/home/HomePopup.js';
import { Button } from '../../components/ui/Button.js';
import { DateRangePicker } from '../../components/ui/datetime/DateRangePicker.js';
import { FIELD_LABEL_CLASS } from '../../components/ui/fieldLabel.js';
import { useAuth } from '../../context/AuthContext.js';

export function HomePage(_props: { path?: string }) {
  // 330 (US4, FR-012): the customer searches their campus. The location picker, its "Any location",
  // the nearest-location guess (125) and the name de-duplication (#438) are gone: only customers with
  // a campus reach this page (AuthGuard), and the platform searches the campus whatever is asked.
  const { user } = useAuth();
  const campusId = user?.campus_location_id ?? '';
  const campusName = user?.campus?.name ?? '';
  // 093: held as instants, not as the native control's zone-less strings — see handleSearch.
  const [pickupDate, setPickupDate] = useState<Date | null>(null);
  const [returnDate, setReturnDate] = useState<Date | null>(null);

  /**
   * 093 FR-015: `toISOString()`, matching `BookingFormPage.tsx:189-190`.
   *
   * This is a behaviour change and it is the point. Until now this form put the native
   * control's local, ZONE-LESS `YYYY-MM-DDTHH:mm` into `pickup_at`; `SearchResultsPage`
   * forwards it verbatim and `cars/routes.ts` parses it with `new Date(raw)` ON THE SERVER,
   * whose container sets no `TZ` and therefore runs UTC. A zone-less date-time is parsed in
   * the PARSER's zone, so someone in Malaysia asking for 14:30 had 14:30 UTC searched —
   * 22:30 their own time. Sending the instant removes the ambiguity, and the searched
   * window moves: from wrong to right.
   *
   * Each date is independent — a search with one, or neither, is valid (FR-016).
   */
  function handleSearch(e: Event) {
    e.preventDefault();
    const params = new URLSearchParams();
    if (campusId) params.set('location_id', campusId);
    if (pickupDate) params.set('pickup_at', pickupDate.toISOString());
    if (returnDate) params.set('return_at', returnDate.toISOString());
    route(`/search?${params.toString()}`);
  }

  return (
    <div className="page-fill bg-bg flex flex-col">
      {/* 389 (#1305): the posters staff publish, over the search, once per device per version. */}
      <HomePopup />
      <div className="flex-1 flex flex-col items-center justify-center px-4 gap-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-text">
            CARii <span className="text-primary">Kereta</span>
          </h1>
          <p className="mt-2 text-gray-400">Find and rent the perfect car</p>
        </div>

        <form
          onSubmit={handleSearch}
          className="w-full max-w-sm bg-surface rounded-2xl p-6 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>Your campus</span>
            <p data-testid="home-campus" className="py-2 text-sm text-text">
              {campusName}
            </p>
          </div>

          {/* 093 FR-014: the same picker the booking form uses. The RANGE picker rather
              than two independent ones, because these two fields are a pickup→return pair
              and it already carries the rule that a return is never before pickup + 30min.
              No minDate: search is a filter, not a booking, and either date may be left
              unset (FR-016). */}
          <DateRangePicker
            pickup={pickupDate}
            return={returnDate}
            onChange={({ pickup, return: ret }) => {
              setPickupDate(pickup);
              setReturnDate(ret);
            }}
          />

          <Button type="submit" size="lg" className="w-full mt-2">
            Search Cars
          </Button>
        </form>
      </div>
    </div>
  );
}
